import { type RefObject, useEffect } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';
import {
	getInputSkeletonFocus,
	getInputSkeletonQuery,
	getInputSkeletonSelection,
	getSessionItem,
	removeInputSkeletonState,
	SEARCH_QUERY_KEY,
	setSessionItem,
} from '@atlassian/search-dialog';

// The query is stored in module scope to persist across remounts
let initialQueryValue: string | undefined;

export const getInitialQuery = () => {
	const stickySearch = fg('quick_find_remove_sticky_search')
		? initialQueryValue
		: getSessionItem(SEARCH_QUERY_KEY);

	return stickySearch || getInputSkeletonQuery() || '';
};

export type InputSkeletonProps = {
	localQuery: string;
	inputRef?: RefObject<HTMLInputElement>;
};

export const useInputSkeleton = ({ localQuery, inputRef }: InputSkeletonProps) => {
	// Focus input if skeleton is focused
	useEffect(() => {
		const skeletonFocused = getInputSkeletonFocus();

		if (skeletonFocused) {
			const input = inputRef?.current;
			if (input) {
				input.focus();
				const { selectionStart, selectionEnd, selectionDirection } = getInputSkeletonSelection();
				input.setSelectionRange(selectionStart, selectionEnd, selectionDirection);
			}
		}

		removeInputSkeletonState();
	}, [inputRef]);

	// Push local query to session storage
	useEffect(() => {
		if (fg('quick_find_remove_sticky_search')) {
			// Update the module-scoped variable to make it available for remounts
			initialQueryValue = localQuery;
		} else {
			setSessionItem(SEARCH_QUERY_KEY, localQuery);
		}
	}, [localQuery]);
};
